import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { GroupCreateUsersTable } from "@/components/pages/users/components/GroupCreateUsersTable";
import { TenantAccessGroupFields } from "@/components/pages/users/components/TenantAccessGroupFields";
import { useHotels } from "@/components/pages/hotels/hooks/useHotels";
import {
  useAccessGroup,
  useUpdateAccessGroup,
} from "@/components/pages/users/hooks/useAccessGroups";
import { Role } from "@/types/user";
import {
  useUsers,
  useUsersCount,
} from "@/components/pages/users/hooks/useUsers";
import { ApiError } from "@/services/BaseService";
import { buildTenantRows } from "@/components/pages/users/utils/userGroupHelpers";

const USERS_PER_PAGE = 10;

interface TenantAccessGroupEditPageProps {
  id: string;
  backTo: string;
  readOnly?: boolean;
}

export const TenantAccessGroupEditPage: React.FC<
  TenantAccessGroupEditPageProps
> = ({ id, backTo, readOnly = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [groupName, setGroupName] = useState("");
  const [description, setDescription] = useState("");
  const [usersSearch, setUsersSearch] = useState("");
  const [debouncedUsersSearch, setDebouncedUsersSearch] = useState("");
  const [hotelSearch, setHotelSearch] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedHotelIds, setSelectedHotelIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [initialized, setInitialized] = useState(false);

  const { data: group, isLoading: isGroupLoading } = useAccessGroup(
    "tenant",
    id,
  );
  const updateMutation = useUpdateAccessGroup("tenant", id);

  useEffect(() => {
    if (group && !initialized) {
      setGroupName(group.name);
      setDescription(group.description ?? "");
      if ("members" in group) {
        setSelectedUserIds(group.members.map((m) => m.userId));
      }
      if ("hotels" in group) {
        setSelectedHotelIds(group.hotels.map((h) => h.hotelId));
      }
      setInitialized(true);
    }
  }, [group, initialized]);

  const onBack = useCallback(() => {
    navigate(backTo);
  }, [backTo, navigate]);

  const tenantUserFilters = useMemo(
    () => ({
      roles: [Role.TENANT_ADMIN, Role.HOTEL_ADMIN, Role.FRONT_DESK, Role.STAFF],
      perPage: USERS_PER_PAGE,
      currentPage,
      search: debouncedUsersSearch || undefined,
    }),
    [currentPage, debouncedUsersSearch],
  );

  const { data: tenantUsersResponse, isLoading: isTenantUsersLoading } =
    useUsers(tenantUserFilters);
  const { data: totalTenantUsers = 0 } = useUsersCount(tenantUserFilters);
  const { data: hotelsResponse, isLoading: isHotelsLoading } = useHotels();

  const hotels = useMemo(
    () => hotelsResponse?.data ?? [],
    [hotelsResponse?.data],
  );
  const tenantUsers = useMemo(
    () => tenantUsersResponse ?? [],
    [tenantUsersResponse],
  );
  const totalPages = Math.max(1, Math.ceil(totalTenantUsers / USERS_PER_PAGE));

  const mapRoleLabel = useCallback(
    (role: Role | "SUPER_ADMIN" | "GUESTERIA_ADMIN") => {
      if (role === Role.FRONT_DESK) return t("users.table.frontDesk");
      if (role === Role.HOTEL_ADMIN) return t("users.table.hotelAdmin");
      if (role === Role.TENANT_ADMIN) return t("users.table.tenantAdmin");
      if (role === Role.STAFF) return t("users.table.staff");
      if (role === "GUESTERIA_ADMIN") {
        return t("users.table.guesteriaAdmin", "Guesteria Admin");
      }
      return role;
    },
    [t],
  );

  const paginatedRows = useMemo(
    () => buildTenantRows(tenantUsers, mapRoleLabel),
    [mapRoleLabel, tenantUsers],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [usersSearch]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedUsersSearch(usersSearch.trim());
    }, 350);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [usersSearch]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const areAllVisibleSelected =
    paginatedRows.length > 0 &&
    paginatedRows.every((row) => selectedUserIds.includes(row.id));

  const toggleUser = useCallback((userId: string) => {
    setSelectedUserIds((current) =>
      current.includes(userId)
        ? current.filter((i) => i !== userId)
        : [...current, userId],
    );
  }, []);

  const toggleAllVisible = useCallback(
    (checked: boolean) => {
      if (checked) {
        const allVisibleIds = paginatedRows.map((row) => row.id);
        setSelectedUserIds((current) =>
          Array.from(new Set([...current, ...allVisibleIds])),
        );
        return;
      }

      const visibleIdsSet = new Set(paginatedRows.map((row) => row.id));
      setSelectedUserIds((current) =>
        current.filter((userId) => !visibleIdsSet.has(userId)),
      );
    },
    [paginatedRows],
  );

  const toggleHotel = useCallback((hotelId: string) => {
    setSelectedHotelIds((current) =>
      current.includes(hotelId)
        ? current.filter((i) => i !== hotelId)
        : [...current, hotelId],
    );
  }, []);

  const toggleAllVisibleHotels = useCallback(
    (hotelIds: string[], checked: boolean) => {
      const visibleHotelIds = new Set(hotelIds);

      setSelectedHotelIds((current) =>
        checked
          ? Array.from(new Set([...current, ...hotelIds]))
          : current.filter((hotelId) => !visibleHotelIds.has(hotelId)),
      );
    },
    [],
  );

  const onSave = async () => {
    const trimmedName = groupName.trim();
    if (!trimmedName) {
      toast.error(
        t("users.groups.create.groupNameRequired", "Group name is required"),
      );
      return;
    }

    try {
      await updateMutation.mutateAsync({
        name: trimmedName,
        description: description.trim() || null,
        memberIds: selectedUserIds,
        hotelIds: selectedHotelIds,
      });
      toast.success(
        t(
          "users.groups.edit.updatedSuccessfully",
          "Access group updated successfully.",
        ),
      );
      navigate(backTo);
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 409) {
        toast.error(
          t(
            "users.groups.edit.nameAlreadyExists",
            "A group with this name already exists.",
          ),
        );
        return;
      }
      const message =
        error instanceof Error
          ? error.message
          : t(
              "users.groups.edit.updateFailed",
              "Failed to update access group",
            );
      toast.error(message);
    }
  };

  if (isGroupLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-[1344px] flex-col gap-6 p-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          onClick={onBack}
          className="text-foreground h-8 rounded-md bg-transparent px-3 py-1 text-base font-normal hover:bg-transparent"
        >
          <Undo2 className="h-6 w-6" />
          {t("users.page.back")}
        </Button>
      </div>

      <div className="bg-background flex flex-col gap-6 rounded-lg p-6">
        <div className="flex flex-col gap-2">
          <Input
            value={groupName}
            onChange={(event) => setGroupName(event.target.value)}
            placeholder={t("users.groups.create.groupNamePlaceholder", "Name")}
            label={t("users.groups.create.groupNameLabel", "Group Name")}
            maxLength={100}
            disabled={readOnly}
          />
          <Textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={t(
              "users.groups.create.descriptionPlaceholder",
              "Optional description",
            )}
            maxLength={500}
            className="min-h-[88px]"
            aria-label={t(
              "users.groups.create.descriptionLabel",
              "Description",
            )}
            disabled={readOnly}
          />
        </div>

        <TenantAccessGroupFields
          usersSearch={usersSearch}
          hotelSearch={hotelSearch}
          hotels={hotels}
          selectedHotelIds={selectedHotelIds}
          isHotelsLoading={isHotelsLoading}
          onUsersSearchChange={setUsersSearch}
          onHotelSearchChange={setHotelSearch}
          onToggleHotel={toggleHotel}
          onToggleAllVisibleHotels={toggleAllVisibleHotels}
          disabled={readOnly}
        />

        <GroupCreateUsersTable
          rows={paginatedRows}
          selectedUserIds={selectedUserIds}
          areAllVisibleSelected={areAllVisibleSelected}
          onToggleUser={toggleUser}
          onToggleAllVisible={toggleAllVisible}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          isLoading={isTenantUsersLoading}
          disabled={readOnly}
        />

        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onBack} className="h-8 px-4">
            {readOnly ? t("users.page.back") : t("users.form.cancel", "Cancel")}
          </Button>
          {readOnly ? null : (
            <Button
              variant="default"
              className="h-8 px-4"
              onClick={() => void onSave()}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending
                ? t("users.form.updating", "Updating...")
                : t("users.groups.create.saveChanges", "Save changes")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
